import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    model,
    PLATFORM_ID,
    signal,
    TemplateRef,
    viewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { TextStyleSize, textStyleClass } from '@sirius/typography';

import {
    DisclosureHeadingLevel,
    DisclosureTriggerContext,
    DisclosureTriggerIcon,
    DisclosureTriggerPosition,
    DisclosureVariant,
} from '../_shared/types/disclosure.type';
import { IconComponent } from '../icon/icon.component';

/**
 * Query hook for hosts that manage focus across several disclosures, such as the accordion's
 * roving keyboard navigation. Structural classes are styling-only and free to change.
 */
export const DISCLOSURE_TRIGGER_SELECTOR = '[data-sirius-disclosure-trigger]';

let _disclosureUid = 0;
@Component({
    selector: 'sirius-disclosure',
    templateUrl: './disclosure.component.html',
    styleUrl: './disclosure.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IconComponent, NgTemplateOutlet, TranslateModule],
    host: {
        'class': 'sir-disclosure sir-text-style-paragraph-m-default',
        '[class.is-expanded]': 'expanded()',
        '[class.is-region]': "variant() === 'region'",
        '[class.is-inline-clamp]': "variant() === 'inline-clamp'",
        '[class.is-clamped]': 'isClamped()',
        '[class.is-trigger-below]': "triggerPosition() === 'below'",
        '[style.--disclosure-clamp]': 'clampLines()',
    },
})
export class DisclosureComponent {
    readonly expanded = model(false);
    readonly variant = input<DisclosureVariant>('region');
    readonly triggerPosition = input<DisclosureTriggerPosition>('above');
    readonly clampLines = input(3);
    readonly triggerIcon = input<DisclosureTriggerIcon>('chevron');

    /**
     * Size step for the trigger label. The category and weight are fixed - a trigger is always
     * emphasised body text - so a caller that needs a larger label moves along the scale rather
     * than restyling it, which a utility class could not be overridden to do from outside.
     */
    readonly labelTextStyleSize = input<TextStyleSize>('m');

    /** Display text, not a translation key. Left empty, the trigger uses its own translated label. */
    readonly collapsedLabel = input('');
    readonly expandedLabel = input('');

    /**
     * Rendered inside the trigger button instead of the label, for hosts that need
     * more than text there. A template rather than projected content because the
     * trigger is stamped either above or below the region.
     */
    readonly triggerContent = input<TemplateRef<DisclosureTriggerContext> | undefined>(undefined);

    /**
     * Wraps the trigger in a heading of this level, as the ARIA accordion pattern
     * expects. Left unset the trigger is a bare button.
     */
    readonly headingLevel = input<DisclosureHeadingLevel | undefined>(undefined);

    private readonly _generatedRegionId = `sir-disclosure-${_disclosureUid++}`;

    /**
     * Settable so a host can point remote controls at the region with `aria-controls`.
     * An empty binding keeps the generated id.
     */
    readonly regionId = input<string, string | undefined>(this._generatedRegionId, {
        transform: (value) => value || this._generatedRegionId,
    });

    /** When set, the panel's `aria-labelledby` points at this heading instead of the button. */
    readonly headingId = input<string | undefined>(undefined);

    private readonly _platformId = inject(PLATFORM_ID);
    private readonly _inner = viewChild<ElementRef<HTMLElement>>('inner');

    protected readonly triggerId = computed(() => `${this.regionId()}-trigger`);
    protected readonly label = computed(() => (this.expanded() ? this.expandedLabel() : this.collapsedLabel()));
    protected readonly labelKey = computed(() => (this.expanded() ? 'disclosure.showLess' : 'disclosure.showMore'));
    protected readonly labelClass = computed(
        () => `disclosure-label ${textStyleClass('paragraph', this.labelTextStyleSize(), 'bold')}`
    );

    /** `region` is an expand/collapse panel and earns a landmark; `inline-clamp` is clipped text. */
    protected readonly isRegion = computed(() => this.variant() === 'region');

    protected readonly regionLabelledBy = computed(() => {
        if (!this.isRegion()) return null;
        const headingId = this.headingId();
        return this.headingLevel() && headingId ? headingId : this.triggerId();
    });

    /**
     * Starts `true` so the trigger survives SSR and pre-measurement; the client hides it again
     * once it has measured that the text fits.
     */
    private readonly _overflowing = signal(true);

    protected readonly showTrigger = computed(() => this.isRegion() || this.expanded() || this._overflowing());

    /** True only while the inline clamp is actually hiding content, which the fade mask keys off. */
    protected readonly isClamped = computed(
        () => this.variant() === 'inline-clamp' && !this.expanded() && this._overflowing()
    );

    private _resizeObserver?: ResizeObserver;

    constructor() {
        const destroyRef = inject(DestroyRef);

        afterNextRender(() => {
            if (this.variant() !== 'inline-clamp') return;

            this.measureOverflow();

            const inner = this._inner()?.nativeElement;
            if (inner && typeof ResizeObserver !== 'undefined') {
                this._resizeObserver = new ResizeObserver(() => this.measureOverflow());
                this._resizeObserver.observe(inner);
            }
        });

        destroyRef.onDestroy(() => this._resizeObserver?.disconnect());
    }

    toggle(): void {
        this.expanded.update((value) => !value);
    }

    /**
     * A clamp only clips its content visually, so anything focusable in the hidden remainder is
     * still tabbable. Expanding on focus keeps that focus on screen.
     */
    protected onRegionFocusIn(): void {
        if (this.isClamped()) {
            this.expanded.set(true);
        }
    }

    /** Only meaningful while collapsed: compare full vs clamped content height. */
    private measureOverflow(): void {
        if (!isPlatformBrowser(this._platformId) || this.expanded()) return;

        const inner = this._inner()?.nativeElement;
        if (!inner) return;

        this._overflowing.set(inner.scrollHeight - inner.clientHeight > 1);
    }
}
